"use client";

import { useState, useRef, useEffect } from "react";
import CategoryCards from "./CategoryCards";
import CategorySearch from "./CategorySearch";
import { Search } from "lucide-react";
import categoriesData from "@/data/categories.json";

// categories.json 구조: { categories: [...] }
const categories = (categoriesData as any).categories || [];

// 모바일 감지 훅
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

interface CategorySelectorProps {
  value?: string;
  onChange?: (category: string) => void;
  onSelect?: (category: any) => void;
  onBack?: () => void;
}

export default function CategorySelector({
  value,
  onChange,
  onSelect,
  onBack,
}: CategorySelectorProps) {
  const [selectedTop, setSelectedTop] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [globalSearchQuery, setGlobalSearchQuery] = useState<string>("");
  const [showGlobalSearch, setShowGlobalSearch] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const globalSearchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const handleItemSelect = (item: string) => {
    console.log("CategorySelector: 악기 선택됨", item);
    setSelectedItem(item);
    if (onChange) {
      onChange(item);
    }
  };

  // 전체 카테고리에서 검색
  const getAllItemsFromAllCategories = () => {
    const allItems: Array<{ name: string; category: string; categoryId: string }> = [];
    
    try {
      if (!Array.isArray(categories) || categories.length === 0) {
        console.warn("카테고리 데이터가 없습니다.");
        return allItems;
      }

      categories.forEach((cat: any) => {
        if (!cat || !cat.items) return;
        
        cat.items.forEach((item: any) => {
          if (typeof item === "string") {
            allItems.push({
              name: item,
              category: cat.name || "",
              categoryId: cat.id || "",
            });
          } else if (item && typeof item === "object" && item.name) {
            // 메인 카테고리 추가
            allItems.push({
              name: item.name,
              category: cat.name || "",
              categoryId: cat.id || "",
            });
            // 서브카테고리 추가
            if (Array.isArray(item.subcategories)) {
              item.subcategories.forEach((sub: string) => {
                if (sub) {
                  allItems.push({
                    name: sub,
                    category: cat.name || "",
                    categoryId: cat.id || "",
                  });
                }
              });
            }
          }
        });
      });
    } catch (error) {
      console.error("카테고리 검색 중 오류 발생:", error);
    }
    
    return allItems;
  };

  const filteredGlobalItems = globalSearchQuery.length > 1
    ? getAllItemsFromAllCategories().filter(item => 
        item.name.toLowerCase().includes(globalSearchQuery.toLowerCase())
      )
    : [];

  // 카테고리 선택 시 검색창 자동 포커스
  useEffect(() => {
    if (selectedTop && searchInputRef.current) {
      // 약간의 지연을 두어 DOM이 업데이트된 후 포커스
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [selectedTop]);

  // 전체 검색 활성화 시 포커스
  useEffect(() => {
    if (showGlobalSearch && globalSearchInputRef.current) {
      setTimeout(() => {
        globalSearchInputRef.current?.focus();
      }, 100);
    }
  }, [showGlobalSearch]);

  const handleGlobalItemSelect = (item: { name: string; category: string; categoryId: string }) => {
    try {
      // 해당 카테고리 찾기
      const category = Array.isArray(categories) 
        ? categories.find((cat: any) => cat && cat.id === item.categoryId)
        : null;
      
      if (category) {
        setSelectedTop(category);
        setSelectedItem(item.name);
        setShowGlobalSearch(false);
        setGlobalSearchQuery("");
      } else {
        console.warn("카테고리를 찾을 수 없습니다:", item.categoryId);
      }
    } catch (error) {
      console.error("카테고리 선택 중 오류 발생:", error);
    }
  };

  return (
    <div className="w-full space-y-6 relative pb-32 md:pb-20">
      {/* 뒤로가기 버튼 (onBack prop이 있을 때만 표시) */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">이전으로</span>
        </button>
      )}

      {/* 전체 검색 버튼 */}
      {!showGlobalSearch && !selectedTop && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowGlobalSearch(true)}
            className="w-full flex items-center gap-2 px-4 py-3 border-2 border-blue-500 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors font-medium"
          >
            <Search className="w-5 h-5" />
            <span>전체 카테고리에서 검색</span>
          </button>
        </div>
      )}

      {/* 전체 검색 모드 */}
      {showGlobalSearch && !selectedTop && (
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={globalSearchInputRef}
                type="text"
                placeholder="카테고리나 악기 이름을 검색하세요..."
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setShowGlobalSearch(false);
                setGlobalSearchQuery("");
              }}
              className="px-4 py-3 text-gray-600 hover:text-gray-800"
            >
              취소
            </button>
          </div>

          {/* 검색 결과 */}
          {globalSearchQuery.length > 1 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">
                검색 결과 ({filteredGlobalItems.length}개)
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
                {filteredGlobalItems.map((item, index) => (
                  <button
                    key={`${item.categoryId}-${item.name}-${index}`}
                    type="button"
                    onClick={() => handleGlobalItemSelect(item)}
                    className="p-3 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 transition-all text-sm font-medium text-center"
                  >
                    <div className="text-xs text-gray-500 mb-1">{item.category}</div>
                    <div>{item.name}</div>
                  </button>
                ))}
              </div>
              {filteredGlobalItems.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  "{globalSearchQuery}"에 대한 검색 결과가 없습니다.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 카테고리 카드들 - 전체 검색 모드가 아닐 때만 표시 */}
      {!showGlobalSearch && (
        <CategoryCards
          onSelect={cat => {
            setSelectedTop(cat);
            setShowGlobalSearch(false);
            setGlobalSearchQuery("");
          }}
          selectedCategory={selectedTop}
        />
      )}

      {/* 선택된 카테고리가 있을 때 검색창과 악기 목록 표시 */}
      {selectedTop && (
        <>
          <CategorySearch
            ref={searchInputRef}
            topCategory={selectedTop}
            onSelectItem={handleItemSelect}
            selectedItem={selectedItem}
          />
        </>
      )}

      {/* 하단 고정 바 */}
      {selectedItem && (
        <div className="fixed left-0 w-full bg-white border-t shadow-md p-4 safe-area-inset-bottom md:bottom-0" style={{ bottom: isMobile ? '68px' : '0', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex flex-col space-y-2">
              <div className="flex flex-wrap gap-2">
                {selectedTop && (
                  <span className="px-2 py-1 bg-gray-200 rounded text-sm">
                    {selectedTop.name}
                  </span>
                )}
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                  {selectedItem}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                악기가 선택되었습니다. 다음 단계에서 거래 유형을 선택하세요.
              </p>
            </div>
            <button
              type="button"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition font-medium"
              onClick={() => {
                const categoryData = {
                  name: selectedItem,
                  categoryPath: [selectedTop?.name, selectedItem].filter(
                    Boolean
                  ),
                  categoryId: `${selectedTop?.id}_${selectedItem.toLowerCase().replace(/\s+/g, "_")}`,
                };
                console.log("CategorySelector: onSelect 호출됨", categoryData);
                if (onSelect) {
                  onSelect(categoryData);
                } else if (onChange) {
                  onChange(selectedItem);
                }
              }}
            >
              다음 단계로 →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
